// @flow
import * as React from 'react'
import shallowEqual from 'mz-utils/shallowEqual'
import {
  formatMessage,
  isMessageReady,
  loadMessage,
} from '../core/formatMessage'

// Types
import type { Message } from '../create'

export type Props = {
  message: string | Message,
  values: Object,
  tagName: string | React.ComponentType<any>,
  children?: any,
  alwaysRerender?: boolean,
  withHTML?: boolean,
  formats?: Object,
}

// Component used to wrap a message when it is not yet loaded
// to be able to render the real conent and reduce interface
// changes when actual translations will be loaded
const TransparentMessage = ({ children }: { children: React.Node }) => (
  <span style={{ color: 'transparent' }}>{children}</span>
)

function getChildrenNodes(
  formattedMessage: string,
  tokenResult: Object
): Array<React.Node> {
  const { elements, tokenDelimiter } = tokenResult
  const hasElements = elements && Object.keys(elements).length > 0

  if (hasElements) {
    // Split the message into parts so the React Element values captured
    // above can be inserted back into the rendered message. This
    // approach allows messages to render with React Elements while
    // keeping React's virtual diffing working properly.
    return formattedMessage
      .split(tokenDelimiter)
      .filter((part) => !!part)
      .map((part) => elements[part] || part)
  }

  return [formattedMessage]
}

/**
 * Forked from `react-intl`, a bit refactored and added ability
 * to load messages asynchronously with showing transparent text
 * (placeholder) while loading.
 */
class FormattedMessage extends React.Component<Props> {
  unmounted = false

  static contextTypes = {
    mzIntl: () => null,
  }

  static defaultProps = {
    values: {},
    tagName: 'span',
  }

  componentDidMount() {
    this.loadMessage()
  }

  componentWillUnmount() {
    this.unmounted = true
  }

  loadMessage() {
    const { message } = this.props
    if (!isMessageReady(message, this.context)) {
      loadMessage(message, this.handleMessageLoaded, this.context)
      return true
    }
    return false
  }

  handleMessageLoaded = () => {
    if (!this.loadMessage() && !this.unmounted) {
      this.forceUpdate()
    }
  }

  shouldComponentUpdate(nextProps: Props) {
    const { values, message } = this.props
    const {
      alwaysRerender,
      children,
      values: nextValues,
      message: nextMessage,
    } = nextProps

    if (
      alwaysRerender ||
      typeof children === 'function' ||
      message !== nextMessage ||
      !shallowEqual(nextValues, values)
    ) {
      return true
    }
    return false
  }

  getTokenizedValues() {
    const { values: propsValues } = this.props
    const { values: messageValues } = (this.props.message: any)
    const hasValues =
      (propsValues && Object.keys(propsValues).length > 0) ||
      (messageValues && Object.keys(messageValues).length > 0)

    if (!hasValues) {
      return {}
    }

    // Create final values object where values from the props has higher
    // proprity than values from the message object
    const values = {
      ...messageValues,
      ...propsValues,
    }

    // Creates a token with a random UID that should not be guessable or
    // conflict with other parts of the `message` string.
    const uid = Math.floor(Math.random() * 0x10000000000).toString(16)

    // Splitting with a delimiter to support IE8. When using a regex
    // with a capture group IE8 does not include the capture group in
    // the resulting array.
    const tokenDelimiter = `@__${uid}__@`
    const tokenizedValues = {}
    const elements = {}

    // Internal token generator for each value
    const generateToken = (() => {
      let counter = 0
      return () => {
        counter += 1
        return `ELEMENT-${uid}-${counter}`
      }
    })()

    // Iterates over the `props` to keep track of any React Element
    // values so they can be represented by the `token` as a placeholder
    // when the `message` is formatted. This allows the formatted
    // message to then be broken-up into parts with references to the
    // React Elements inserted back in.
    Object.keys(values).forEach((name) => {
      const value = values[name]

      if (React.isValidElement(value)) {
        const token = generateToken()
        tokenizedValues[name] = tokenDelimiter + token + tokenDelimiter
        elements[token] = value
      } else {
        tokenizedValues[name] = value
      }
    })

    return { tokenDelimiter, tokenizedValues, elements }
  }

  renderHtmlNodes(nodes: Array<any>): Array<React.Node> {
    if (!this.props.withHTML) return nodes
    return nodes.map((node) =>
      !React.isValidElement(node) ? (
        <span dangerouslySetInnerHTML={{ __html: node }} />
      ) : (
        node
      )
    )
  }

  renderMessage(): React.Node {
    const {
      message: descriptor,
      tagName: Component,
      children,
      formats,
    } = this.props

    // Format message with values replaced with tokens that will
    // be used to replace with actual react components
    const tokenResult = this.getTokenizedValues()
    const formattedMessage = formatMessage(
      descriptor,
      tokenResult.tokenizedValues,
      this.context,
      formats
    )
    const nodes = getChildrenNodes(formattedMessage, tokenResult)

    // Use render children function (if provided) or react createElement.
    // It uses `createElement` directly to avoid `key` warnings.
    return typeof children === 'function'
      ? children(...nodes)
      : React.createElement(Component, null, ...this.renderHtmlNodes(nodes))
  }

  render() {
    const { message, children, tagName: Component } = this.props

    if (!isMessageReady(message, this.context)) {
      return typeof children !== 'function' ? (
        <Component>
          <TransparentMessage>{this.renderMessage()}</TransparentMessage>
        </Component>
      ) : (
        children('')
      )
    }

    return this.renderMessage()
  }
}

export default FormattedMessage
